import { useEffect, useState } from "react";
import type {
  CommentStatus,
  VisibilityChange,
  WorkspaceMember,
} from "../../api/types";
import type { RebaseState } from "../../api/articleUnits";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  History,
  PenLine,
  Reply,
  Trash2,
} from "../../compose/Icon";
import type { AnchorState } from "../../lib/anchoring";
import { actorFromLegacy } from "../../lib/avatar";
import {
  type CommentVersionView,
  commentVersionView,
  type VersionTickLike,
} from "../../lib/commentVersionState";
import { VersionRibbon } from "./VersionRibbon";
import { VersionSwitchPopover } from "./VersionSwitchPopover";
import { formatDate, formatDateTime } from "../../lib/format";
import type { ThreadNode } from "../../lib/threads";
import {
  defaultThreadExpanded,
  replyCount,
  threadToggleAriaLabel,
} from "../../lib/threadCollapse";
import { Avatar } from "../shared/Avatar";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { CommentBody } from "./CommentBody";
import { CommentCardBar } from "./CommentCardBar";
import { CommentComposer } from "./CommentComposer";
import { QuoteCanvas } from "./QuoteCanvas";
import { quoteRegionActivates } from "./quoteSurface";
import { ReactionBar } from "./ReactionBar";

/**
 * One thread. The ROOT renders as the redesigned 5-row `.comment-card`
 * (rich-comments §11): Row-1 status+type bar (pin · type-colored pill · Resolve ·
 * ⋯ menu · copy-link), Row-2 author | date·time, Row-3 full-width type-colored
 * quote, Row-4 the RICH body, Row-5 reply. The card's whole identity is its TYPE
 * COLOR (§12) — a type-tinted left edge + bar + quote, matching its doc
 * highlight. Hovering the card brightens that highlight; hovering the highlight
 * raises the card (§13). Replies keep the lighter nested treatment.
 */
export interface ThreadCallbacks {
  members: WorkspaceMember[];
  onReply: (
    parentId: number,
    draft: { body: string; body_format: string; mentions: number[] },
  ) => Promise<void>;
  onStatus: (commentId: number, status: CommentStatus) => Promise<void>;
  onFocusAnchor: (commentId: number) => void;
  anchorStates: Record<number, AnchorState>;
  /** W3 (§1.7): the SERVER version-aware anchor state per comment (the durable
   *  commit-time `docz.anchor.rebase` outcome + the section version it resolves
   *  against). When present it OVERRIDES the client-side re-anchor: repaired →
   *  "moved in v{n}", orphaned → struck-through. */
  rebaseStates?: Record<number, { state: RebaseState; seq: number }>;
  /** Workspace slug + document id — the stable permalink base for a comment's
   *  "Copy link" action (permalink-citations). */
  slug: string;
  docId: string;
  onCopyLink?: (url: string) => void;
  // ── comment-visibility (§6A): the viewer identity (drives the pill's author-
  //    or-staff `canEdit`) + the change-scope handler. When `onVisibility` is
  //    absent the pill is not rendered (read-only surfaces). ──────────────────
  viewerId?: number;
  viewerIsStaff?: boolean;
  onVisibility?: (commentId: number, body: VisibilityChange) => Promise<void>;
  // ── review-notes enhancements §3 (author edit) ────────────────────────────
  /** Persist an author body edit (author-only — enforced server-side + in the
   *  mock). Absent ⇒ no edit affordance is shown (read-only surfaces). */
  onEdit?: (
    commentId: number,
    draft: { body: string; body_format: string },
  ) => Promise<void>;
  // ── review-notes enhancements §4 (Slack-style emoji reactions) ────────────
  /** Toggle the viewer's `emoji` reaction on a comment/reply (`add` true ⇒ react,
   *  false ⇒ un-react). Absent ⇒ the ReactionBar is not rendered (read-only /
   *  unwired surfaces). The parent owns the optimistic + SSE reconcile. */
  onToggleReaction?: (commentId: number, emoji: string, add: boolean) => void;
  // ── review-notes enhancements §2.3 (deep-link auto-expand) ────────────────
  /** A deep-linked comment/reply id: its thread auto-expands so the reveal lands
   *  even though reply threads collapse by default. */
  revealCommentId?: number | null;
  /** ticket / permalink deep-link: when this equals a ROOT card's comment id, the
   *  card renders the one-shot 3s slow-fade highlight (`comment-card--reveal`).
   *  State-driven (never an imperative class toggle — React re-renders clobber
   *  that); the host clears it after the fade. */
  flashCommentId?: number | null;
  /** rich-comments §13: the comment whose card must LIFT (a doc highlight is
   *  hovered, span→card). */
  linkedCommentId?: number | null;
  /** rich-comments §13.1: card→span — fired on card hover enter/leave so the host
   *  brightens the matching doc highlight span. */
  onHoverCard?: (commentId: number | null) => void;
  // ── comment-delete ────────────────────────────────────────────────────────
  /** Delete a comment/reply (AUTHOR-ONLY — enforced server-side AND in the mock).
   *  Absent ⇒ no delete affordance is shown (read-only surfaces). The host owns
   *  the confirm + the re-fetch: a deleted note with no replies DISAPPEARS, while
   *  one with replies returns as a `deleted` placeholder. */
  onDelete?: (commentId: number) => Promise<void>;
  // ── comment-version ribbon + switch (owner 2026-07-28) ─────────────────────
  /** The document's version ticks for THIS pane — the same list the timeline
   *  renders. A comment's `version_id` is resolved against it, so there is one
   *  version vocabulary. Empty/absent ⇒ no ribbon, no switch (fail-safe). */
  versionTicks?: readonly VersionTickLike[];
  /** The version currently ON SCREEN (`null` ⇒ the live head) — the yardstick a
   *  comment's own version is compared against. */
  displayedVersionId?: string | null;
  /** Switch the pane to `versionId`. MUST be wired to the same handler the
   *  timeline's `onView` uses (`ArticleWorkbench.onViewVersion`) — never a second
   *  switch implementation. Absent ⇒ the quote keeps its plain jump-to-anchor
   *  behaviour even on an obsolete note (an affordance that cannot do anything is
   *  worse than none). */
  onSwitchVersion?: (versionId: string) => void;
}

/**
 * The quote badge. web-docz's `AnchorState` describes a HIGHLIGHT that exists in
 * the rendered document (exact / moved / orphan); a comment that never anchored
 * has no highlight at all, so it gets a fourth, local value rather than being
 * squeezed into one of the three.
 */
export type QuoteBadge = AnchorState | "unanchored";

/**
 * Map the server anchor state to the badge the quote button styles.
 *
 * ⛔ This was `if exact … if orphaned … return "moved"` — a trailing default. So
 * `unanchored`, the state that exists precisely to say "this comment has no
 * anchor", rendered as **moved**: a confident claim that the quote is in the
 * document, just elsewhere. That is the exact lie the server-side anchor-honesty
 * change was made to kill, restated one layer up.
 *
 * The `switch` is exhaustive with NO default. A new `RebaseState` member is a
 * compile error here, not a silent "moved".
 */
function badgeOf(state: RebaseState): QuoteBadge {
  switch (state) {
    case "exact":
      return "exact";
    case "shifted":
    case "repaired":
      return "moved";
    case "orphaned":
      return "orphan";
    case "unanchored":
      return "unanchored";
  }
}

export function CommentThread({
  node,
  depth,
  cb,
}: {
  node: ThreadNode;
  depth: number;
  cb: ThreadCallbacks;
}) {
  const { comment } = node;
  const isRoot = depth === 0;
  const [replying, setReplying] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // review-notes §2: reply threads collapse by default; a deep-linked reply
  // auto-expands its thread (§2.3). Only ROOT threads carry the toggle.
  const [expanded, setExpanded] = useState(() =>
    defaultThreadExpanded(node, cb.revealCommentId),
  );
  // A late-arriving deep link (the reveal id lands after mount) opens the thread;
  // it never re-collapses (a collapsed thread must not swallow the target).
  useEffect(() => {
    if (defaultThreadExpanded(node, cb.revealCommentId)) setExpanded(true);
  }, [node, cb.revealCommentId]);

  // review-notes §3: the viewer may edit ONLY their own note (root OR reply),
  // and only when a persist handler is wired.
  const canEdit =
    cb.onEdit != null &&
    cb.viewerId != null &&
    comment.author_id === cb.viewerId;
  // §3.3: `edited` derives purely from the timestamps — a bumped `updated_at`
  // (author body edit) that differs from `created_at`.
  const edited =
    comment.updated_at != null &&
    comment.updated_at !== "" &&
    comment.updated_at !== comment.created_at;

  const editedTag = edited ? (
    <span
      className="comment-card__edited"
      data-testid={`comment-edited-indicator-${comment.id}`}
      title={formatDateTime(comment.updated_at ?? null)}
    >
      · edited
    </span>
  ) : null;
  // W3: prefer the SERVER version-aware state (durable, commit-time rebase) over
  // the client-side re-anchor when the backend supplied it.
  const server = cb.rebaseStates?.[comment.id];
  const anchorState: QuoteBadge = server
    ? badgeOf(server.state)
    : (cb.anchorStates[comment.id] ?? "exact");
  // A comment that never anchored is in the same position as an orphaned one for
  // the READER — its quote is not in this document and clicking it goes nowhere —
  // so the quote is struck through in both cases. They are NOT given the same
  // words, because they are not the same fact: one lost its anchor, the other
  // never had one.
  const unanchored = anchorState === "unanchored";
  const orphaned = anchorState === "orphan";
  const notInDocument = orphaned || unanchored;

  // ── the comment↔version axis (INDEPENDENT of the anchor axis above) ────────
  // `anchorState` answers "did my TEXT move?"; this answers "which VERSION do I
  // cite?". A `repaired` note is anchored to the current version and is NOT
  // obsolete; a perfectly `exact` note written against v3 while you read v7 IS.
  // Both are rendered, because they tell the reader two different things.
  const vv: CommentVersionView = commentVersionView(
    comment,
    cb.versionTicks ?? [],
    cb.displayedVersionId ?? null,
  );
  // The switch affordance requires BOTH a version to go to and a handler that can
  // go there. Without a handler the quote keeps its plain jump-to-anchor
  // behaviour — an affordance that cannot do anything is worse than none.
  const canSwitch = vv.offerSwitch && vv.bound != null && cb.onSwitchVersion != null;
  const behind =
    vv.bound != null && vv.displayed != null && vv.bound.seq < vv.displayed.seq;
  const [switchOpen, setSwitchOpen] = useState(false);

  // comment-delete: a tombstone the server kept ONLY because living replies hang
  // off it. Its content is already blanked server-side — the card must render a
  // placeholder and MUST NOT offer edit/reply/react/resolve on a note that no
  // longer says anything.
  const isDeleted = comment.deleted === true;
  const canDelete =
    !isDeleted &&
    cb.onDelete != null &&
    cb.viewerId != null &&
    comment.author_id === cb.viewerId;

  const changeStatus = async (next: CommentStatus) => {
    setStatusBusy(true);
    try {
      await cb.onStatus(comment.id, next);
    } finally {
      setStatusBusy(false);
    }
  };

  // comment-delete: destructive and irreversible, so it goes through the SAME
  // `<ConfirmDialog>` every other destructive action in the app uses (RULE ZERO —
  // no bespoke confirm). The dialog names the consequence honestly, including the
  // one thing a user cannot guess: deleting a thread root does NOT take the
  // replies with it.
  const runDelete = () => setConfirmingDelete(true);
  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await cb.onDelete?.(comment.id);
      setConfirmingDelete(false);
    } finally {
      setDeleteBusy(false);
    }
  };

  const replyBox = replying ? (
    <div className="thread-comment__reply-box" data-testid="reply-box">
      <CommentComposer
        members={cb.members}
        showType={false}
        autoFocus
        submitLabel="Reply"
        testid={`reply-composer-${comment.id}`}
        onCancel={() => setReplying(false)}
        onSubmit={async (draft) => {
          await cb.onReply(comment.id, {
            body: draft.body,
            body_format: draft.body_format,
            mentions: draft.mentions,
          });
          setReplying(false);
        }}
      />
    </div>
  ) : (
    <button
      type="button"
      className="thread-comment__reply-btn"
      data-testid={`reply-button-${comment.id}`}
      onClick={() => setReplying(true)}
    >
      <Reply size={13} strokeWidth={1.5} />
      Reply
    </button>
  );

  // review-notes §3: the in-place editor — reuses the rich `<CommentComposer>`
  // seeded with the current fragment (showType off, "Save"/"Cancel"). It replaces
  // ONLY the body row; the card's bar/meta/quote stay put while editing.
  const editComposer = (
    <div className="comment-edit" data-testid={`comment-edit-composer-${comment.id}`}>
      <CommentComposer
        members={cb.members}
        showType={false}
        autoFocus
        submitLabel="Save"
        testid={`comment-edit-editor-${comment.id}`}
        submitTestid={`comment-edit-save-${comment.id}`}
        cancelTestid={`comment-edit-cancel-${comment.id}`}
        initialBody={comment.body}
        initialFormat={comment.body_format}
        onCancel={() => setEditing(false)}
        onSubmit={async (draft) => {
          await cb.onEdit?.(comment.id, {
            body: draft.body,
            body_format: draft.body_format,
          });
          setEditing(false);
        }}
      />
    </div>
  );

  // review-notes §4: the Slack-style reaction bar (chips + count + picker +
  // who-popup). Rendered only when a toggle handler is wired (read-only surfaces
  // omit it). Both the root (Row-4b) and the reply variant consume it.
  const reactionBar = cb.onToggleReaction ? (
    <ReactionBar
      cid={comment.id}
      reactions={comment.reactions ?? []}
      onToggleReaction={cb.onToggleReaction}
      compact={!isRoot}
    />
  ) : null;

  const hasReplies = node.replies.length > 0;
  const replyN = replyCount(node);

  const replies = hasReplies ? (
    <div className="thread-comment__replies">
      {node.replies.map((r) => (
        <CommentThread key={r.comment.id} node={r} depth={depth + 1} cb={cb} />
      ))}
    </div>
  ) : null;

  // review-notes §2: the root's collapsible reply block — a `Thread (N) ›`
  // toggle (default collapsed) above the replies, which are REMOVED from the DOM
  // when collapsed (out of the a11y tree + tab order; the count conveys them).
  const collapsibleReplies = hasReplies ? (
    <>
      <button
        type="button"
        className="thread-collapse"
        data-testid={`thread-toggle-${comment.id}`}
        aria-expanded={expanded}
        aria-controls={`thread-replies-${comment.id}`}
        data-expanded={expanded}
        aria-label={threadToggleAriaLabel(expanded, replyN)}
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? (
          <ChevronDown size={13} strokeWidth={1.75} aria-hidden="true" />
        ) : (
          <ChevronRight size={13} strokeWidth={1.75} aria-hidden="true" />
        )}
        <span className="thread-collapse__label">Thread</span>
        <span
          className="thread-collapse__count"
          data-testid={`thread-reply-count-${comment.id}`}
        >
          {replyN}
        </span>
      </button>
      {expanded ? (
        <div
          className="thread-comment__replies"
          id={`thread-replies-${comment.id}`}
          data-testid={`thread-replies-${comment.id}`}
        >
          {node.replies.map((r) => (
            <CommentThread key={r.comment.id} node={r} depth={depth + 1} cb={cb} />
          ))}
        </div>
      ) : null}
    </>
  ) : null;

  // comment-delete: what a tombstone renders instead of a body. Deliberately
  // plain text and not a styled "error" — a deleted note is a normal, expected
  // state of a thread, and the only thing the reader needs is why the gap is
  // there. It carries NO author words, no quote, no reactions.
  const deletedPlaceholder = (
    <p
      className="comment-card__deleted"
      data-testid={`comment-deleted-${comment.id}`}
    >
      <Trash2 size={12} strokeWidth={1.75} aria-hidden="true" />
      This comment was deleted.
    </p>
  );

  // ── a REPLY (depth > 0): the lighter nested treatment (no card chrome) ──────
  if (!isRoot) {
    return (
      <div
        className={`thread-comment thread-comment--d${Math.min(depth, 3)}`}
        data-testid={`reply-${comment.id}`}
        data-comment-id={comment.id}
        data-status={comment.status}
        data-state={vv.state}
        data-deleted={isDeleted ? "" : undefined}
      >
        <div className="thread-comment__head">
          <span className="thread-comment__reply-glyph" aria-hidden="true">
            <CornerDownRight size={13} strokeWidth={1.5} />
          </span>
          <Avatar actor={comment.author ?? actorFromLegacy(comment.author_name)} size="xs" />
          <span className="thread-comment__author">{comment.author_name}</span>
          {canEdit && !editing && !isDeleted ? (
            <button
              type="button"
              className="thread-comment__edit"
              data-testid={`reply-edit-${comment.id}`}
              aria-label="Edit reply"
              title="Edit"
              onClick={() => setEditing(true)}
            >
              <PenLine size={12} strokeWidth={1.75} />
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              className="thread-comment__delete"
              data-testid={`reply-delete-${comment.id}`}
              aria-label="Delete reply"
              title="Delete"
              onClick={() => runDelete()}
            >
              <Trash2 size={12} strokeWidth={1.75} />
            </button>
          ) : null}
          <span className="thread-comment__time">
            {formatDate(comment.created_at)}
            {editedTag}
          </span>
        </div>
        {isDeleted ? (
          deletedPlaceholder
        ) : editing ? (
          editComposer
        ) : (
          <CommentBody body={comment.body} body_format={comment.body_format} mentions={comment.mentions} />
        )}
        {isDeleted ? null : reactionBar}
        {replies}
        {isDeleted ? null : replyBox}
        {confirmingDelete ? (
          <ConfirmDialog
            title="Delete this reply?"
            body="Your reply text will be removed for everyone. This cannot be undone."
            confirmLabel="Delete"
            danger
            busy={deleteBusy}
            onConfirm={() => void confirmDelete()}
            onCancel={() => setConfirmingDelete(false)}
            testid={`comment-delete-confirm-${comment.id}`}
          />
        ) : null}
      </div>
    );
  }

  // ── Row 3 (the quote) ──────────────────────────────────────────────────────
  //
  // THE QUOTE IS PAINTED BY THE DOCUMENT ENGINE (owner decision D-E: no DOM
  // renders any docz IR anywhere). It used to be `comment.quote` — a plain string
  // off the wire — handed to React as a text child and painted by
  // `annotations.css` at 12px italic in the card's type ink. The passage it names
  // is painted by `pdfz-layout.wasm` in the document's face at the document's body
  // size on paper. So the ONE row whose entire job is "this comment is about THAT
  // passage" showed the reviewer two different renderings of the same words, and
  // the reviewer had to take it on trust that they were the same words.
  // `<QuoteCanvas>` lays it out through the same engine, the same page template
  // and the same font set as the passage — see `quoteSurface.ts` for how a plain
  // string becomes paintable docz IR, and for what a canvas quote costs.
  //
  // ROW-3 IS NO LONGER ONE BIG `<button>`, and it cannot be: every `<canvas>` is
  // `aria-hidden` so the button would have lost its accessible name, the mirror's
  // `role="document"` cannot live inside a button, and — decisively — a
  // drag-select inside a button still fires `click` on release, so selecting the
  // quote in order to COPY it would have jumped the document away mid-gesture,
  // every time. The row is a region; the jump is its own named control, which is
  // ALSO the keyboard path and the version-switch popover's anchor. The painted
  // words stay clickable for pointer users (`onActivate`, suppressed while a
  // selection is live), and `data-testid="comment-anchor-N"` is unchanged, so
  // every caller that drove the old button still drives the new one.
  //
  // The RIBBON stays a flex sibling of the quote text, never an overlay: the text
  // box is laid out AROUND it, so it can neither cover nor truncate the quote, and
  // a long quote in a narrow pane wraps beside it.
  //
  // The typographic `“…”` are GONE from the painted text, deliberately. They are
  // not in the document, and once the box is a rendering of the passage rather
  // than a label, punctuation the passage does not contain makes the identity
  // claim false — and would be carried into the reviewer's clipboard by the
  // IR-sliced copy. The quotation is signalled by the box, not by glyphs.
  //
  // The two meanings a click on this row has always had, in one place:
  //  · a normal note  → JUMP to the anchored text on the canvas;
  //  · a note citing another version → OPEN THE SWITCH POPUP (jumping into text
  //    that belongs to a version you are not looking at would land the reader in
  //    the wrong place, or nowhere).
  // Guarded on the CURRENT open state rather than forced: `@norkron/web-dismiss`
  // closes the popover on outside-click BEFORE this fires, so an unconditional
  // `setSwitchOpen(true)` would immediately re-open it and a second click on the
  // painted quote could never dismiss it.
  const activateQuote = (): void => {
    if (!canSwitch) {
      cb.onFocusAnchor(comment.id);
      return;
    }
    if (!switchOpen) setSwitchOpen(true);
  };

  // ONE string for the control's accessible name AND its tooltip. They were two
  // copies of the same sentence, which is how a label and a tooltip drift into
  // saying different things about the same button.
  const jumpLabel =
    canSwitch && vv.bound
      ? `This note cites ${vv.bound.label} — switch the document to it`
      : "Jump to the anchored text";

  const jumpButton = (
    <button
      type="button"
      className="comment-card__quote-jump"
      data-testid={`comment-anchor-${comment.id}`}
      onClick={() => {
        // ONLY the jump lives here. When `canSwitch`, this button is the
        // `<PopoverTrigger asChild>`, and the trigger already owns the open
        // state — forcing `setSwitchOpen(true)` here as well would fight it:
        // the trigger's toggle would close the popup and this would immediately
        // re-open it, so a second click on the quote could never dismiss.
        if (!canSwitch) cb.onFocusAnchor(comment.id);
      }}
      // The button's name is the ACTION, not the words. The words are read off the
      // `DocumentMirror` inside the quote box, which precedes this control in both
      // DOM and tab order — so a screen-reader user hears the quote and then what
      // pressing this does. Naming it with the quote as well would read the same
      // sentence twice, the exact failure the mirror's `exposure` option exists to
      // prevent.
      aria-label={jumpLabel}
      title={jumpLabel}
    >
      {canSwitch ? (
        <History size={13} strokeWidth={1.75} aria-hidden="true" />
      ) : (
        <ArrowRight size={13} strokeWidth={1.75} aria-hidden="true" />
      )}
    </button>
  );

  // ── THE WHOLE ROW IS THE TARGET (owner, 2026-07-30) ────────────────────────
  //
  // "the ENTIRE `comment-card__quote` MUST be clickable not just `pl-page`
  // `pl-tile-text` and `comment-card__quote-jump`!" Before this, exactly two
  // things responded: the painted canvas and the jump button. The row's padding,
  // the gutter beside the type-ink edge, the blank paper right of a short quote
  // (the surface box is TRIMMED to the ink, so that space is row, not surface)
  // and the entire `text`-state fallback `<p>` were all inert.
  //
  // `quoteRegionActivates` is what keeps that from becoming a double-fire: the
  // painted surface's own listener carries the DRAG GUARD, and the jump button is
  // the popover trigger. See `quoteSurface.ts` for both.
  //
  // ⛔ NOT `role="button"` / `tabIndex={0}` on this div, and that is not laziness:
  //  · it CONTAINS a real `<button>` and the mirror's `role="document"`, and
  //    interactive/document content inside a `role="button"` is not something an
  //    accessibility tree can express (the same three reasons Row-3 stopped being
  //    one big `<button>` — see `QuoteCanvas.tsx`);
  //  · `.comment-card__quote-jump` ALREADY is the keyboard route: a real
  //    `<button>`, a real accessible name (`jumpLabel`), a real `:focus-visible`
  //    ring, in tab order, immediately after the quote's own mirror. A second tab
  //    stop on one action is a WCAG 2.4.3 regression dressed up as an improvement.
  // So this is a POINTER affordance over an action that is already keyboard-
  // reachable and announced — and, being a non-focusable div, it also cannot
  // paint a focus ring on a mouse click (owner bug 4 stays fixed).
  const quoteRow = (
    // biome-ignore lint/a11y/noStaticElementInteractions: the keyboard+AT route to this action is the named `.comment-card__quote-jump` button inside; this is a pointer-only enlargement of the same target. See the note above.
    <div
      className={`comment-card__quote comment-card__quote--${anchorState}`}
      data-testid={`comment-quote-${comment.id}`}
      data-anchor-state={server?.state ?? anchorState}
      data-type={comment.type}
      data-offers-switch={canSwitch ? "" : undefined}
      onClick={(e) => {
        if (quoteRegionActivates(e.target)) activateQuote();
      }}
    >
      <span className="comment-card__quote-row">
        <span className="comment-card__quote-text">
          <QuoteCanvas
            quote={comment.quote}
            // "Not in this document" rides as a real docz `strikeout` MARK, so the
            // ENGINE paints the line and the accessible projection emits `<s>` --
            // the same fact the CSS `<s>` used to state, now in the IR.
            //
            // `notInDocument`, NOT `orphaned` (integration 2026-07-30). The canvas
            // quote was written against a base where `orphaned` was the only "your
            // quote is not here" state; `main` then added a FOURTH anchor state,
            // `unanchored` — a comment that never anchored at all — and struck the
            // quote for BOTH, because for the reader they are the same fact: these
            // words are not in this document and the jump goes nowhere. Painting
            // `struck={orphaned}` here would have taken the canvas half of that
            // change and dropped the anchor-honesty half, so a never-anchored note
            // would have rendered its quote as ordinary, present prose — which is
            // the exact lie the `unanchored` state was introduced to kill. The two
            // states still get DIFFERENT WORDS: the `detached` / `never anchored`
            // flags below are unchanged and remain distinct.
            struck={notInDocument}
            testId={`quote-canvas-${comment.id}`}
            label={`Quoted from the document, comment ${comment.id}`}
            onActivate={activateQuote}
          />
          {server?.state === "repaired" ? (
            <span className="thread-comment__flag">moved in v{server.seq}</span>
          ) : anchorState === "moved" ? (
            <span className="thread-comment__flag">moved</span>
          ) : null}
          {orphaned ? <span className="thread-comment__flag">detached</span> : null}
          {unanchored ? (
            <span className="thread-comment__flag">never anchored</span>
          ) : null}
        </span>
        {vv.showRibbon && vv.bound ? (
          <VersionRibbon
            commentId={comment.id}
            version={vv.bound}
            behind={behind}
          />
        ) : null}
        {canSwitch && vv.bound ? (
          <VersionSwitchPopover
            commentId={comment.id}
            version={vv.bound}
            open={switchOpen}
            onOpenChange={setSwitchOpen}
            onSwitch={() => cb.onSwitchVersion?.(vv.bound!.versionId)}
          >
            {jumpButton}
          </VersionSwitchPopover>
        ) : (
          jumpButton
        )}
      </span>
    </div>
  );

  // ── the ROOT: the redesigned 5-row type-colored card (§11) ──────────────────
  const raised = cb.linkedCommentId === comment.id;
  // ticket / permalink deep-link: this root card is the flash target → render the
  // one-shot 3s slow-fade highlight class (host clears `flashCommentId` after 3s).
  const flashing = cb.flashCommentId != null && cb.flashCommentId === comment.id;
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover linking is a pointer emphasis affordance (keyboard uses the quote-jump button).
    <div
      className={`comment-card${flashing ? " comment-card--reveal" : ""}`}
      data-testid={`comment-${comment.id}`}
      data-comment-id={comment.id}
      data-reveal={flashing ? "" : undefined}
      data-type={comment.type}
      data-status={comment.status}
      /* The THREE distinguishable card states (open-current | open-obsolete |
         resolved). One attribute drives all of the styling, so the CSS can never
         disagree with the derivation — and neither state is signalled by colour
         or opacity alone (resolved keeps its "Resolved" pill text + check glyph;
         open-obsolete carries the literal `vX` ribbon). */
      data-state={vv.state}
      data-deleted={isDeleted ? "" : undefined}
      data-linked-raise={raised ? "" : undefined}
      onMouseEnter={() => cb.onHoverCard?.(comment.id)}
      onMouseLeave={() => cb.onHoverCard?.(null)}
    >
      {/* Row 1 — status+type bar (+ the comment-visibility scope pill, §6A) */}
      <CommentCardBar
        commentId={comment.id}
        type={comment.type}
        status={comment.status}
        busy={statusBusy}
        onStatus={(next) => void changeStatus(next)}
        slug={cb.slug}
        docId={cb.docId}
        onCopyLink={cb.onCopyLink}
        visibility={comment.visibility ?? "public"}
        sharedWith={comment.shared_with ?? []}
        members={cb.members}
        canEditVisibility={
          cb.viewerIsStaff === true ||
          (cb.viewerId != null && comment.author_id === cb.viewerId)
        }
        onVisibility={
          cb.onVisibility
            ? (body) => cb.onVisibility!(comment.id, body)
            : undefined
        }
        canEdit={canEdit && !isDeleted}
        onEdit={canEdit && !isDeleted ? () => setEditing(true) : undefined}
        canDelete={canDelete}
        onDelete={canDelete ? () => void runDelete() : undefined}
        deleted={isDeleted}
      />

      {/* Row 2 — [avatar author] ……… [date·time (· edited)]. The avatar + author
          form ONE left cluster (adjacent, vertically centered); the time is pushed
          to the far right. Grouping them prevents the author name being stranded
          in the middle by a 3-way space-between. */}
      <div className="comment-card__meta">
        <span className="comment-card__meta-who">
          <Avatar actor={comment.author ?? actorFromLegacy(comment.author_name)} size="sm" />
          <span className="comment-card__author">{comment.author_name}</span>
        </span>
        <span className="comment-card__time">
          {formatDateTime(comment.created_at)}
          {editedTag}
        </span>
      </div>

      {/* Row 3 — the anchored quote, full-width + type-colored. Two possible
          click meanings, and the card must not be ambiguous about which it has:
          · normal note  → JUMP to the anchored text on the canvas;
          · note citing another version → OPEN THE SWITCH POPUP (jumping to text
            that belongs to a version you are not looking at would silently land
            the reader in the wrong place, or nowhere). */}
      {comment.quote ? quoteRow : null}

      {/* Row 4 — the rich body (swapped for the in-place editor while editing,
          and for the tombstone placeholder when the note was deleted). */}
      {isDeleted ? (
        deletedPlaceholder
      ) : editing ? (
        editComposer
      ) : (
        <CommentBody body={comment.body} body_format={comment.body_format} mentions={comment.mentions} />
      )}

      {/* Row 4b — review-notes §4 emoji reactions. A tombstone has none, and must
          not offer to add one: reacting to a note that no longer says anything is
          meaningless, and the server has already dropped its reaction rows. */}
      {isDeleted ? null : reactionBar}

      {/* review-notes §2 — collapsible reply thread (default collapsed) */}
      {collapsibleReplies}

      {/* Row 5 — reply. A tombstone takes no new replies: the thread is only kept
          alive by the replies that already exist, and inviting more would grow a
          conversation hanging off a note nobody can read. */}
      {isDeleted ? null : replyBox}

      {confirmingDelete ? (
        <ConfirmDialog
          title="Delete this comment?"
          body={
            replyN > 0
              ? "Your comment text will be removed for everyone. The replies underneath it stay \u2014 they belong to the people who wrote them."
              : "Your comment text will be removed for everyone. This cannot be undone."
          }
          confirmLabel="Delete"
          danger
          busy={deleteBusy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setConfirmingDelete(false)}
          testid={`comment-delete-confirm-${comment.id}`}
        />
      ) : null}
    </div>
  );
}
